/**
 * Ввод подписанного договора в работу (миграция 0259).
 *
 * Юрист согласовал договор — дальше его исполняют другие службы: бухгалтерия
 * платит, снабжение принимает технику, стройка подписывает акт. Кнопка
 * раскладывает обязательства из «Порядка исполнения» по службам компании и
 * уведомляет их; таблица ниже показывает, кто уже принял договор в работу.
 *
 * Состав служб и распределение обязательств настраивает администратор
 * (Администрация → Службы) — здесь только отправка и контроль.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import {
  useAckContractHandoffMutation,
  useHandOffContractMutation,
  useListContractHandoffsQuery,
} from '@shared/api';
import type { HandoffResult } from '@entities/notification/types';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Intro = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const BriefLink = styled(Link)`
  color: inherit;
  text-decoration: underline;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const AckControls = styled.div`
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AckInput = styled.input`
  padding: 4px 8px;
  font-size: 12px;
  font-family: inherit;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Note = styled.div<{ $tone: 'ok' | 'warn' | 'err' }>`
  font-size: 12.5px;
  color: ${({ $tone, theme }) =>
    $tone === 'err'
      ? theme.colors.status.danger
      : $tone === 'warn'
        ? theme.colors.status.warning
        : theme.colors.status.success};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    white-space: nowrap;
  }
`;

const Duty = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};

  & + & {
    margin-top: 2px;
  }
`;

function when(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function errorText(err: unknown, fallback: string): string {
  const msg = (err as { data?: { error?: { message?: string } } })?.data?.error?.message;
  return msg && msg.trim() ? msg : fallback;
}

export function ContractHandoffBlock({
  contractId,
  canWrite,
}: {
  contractId: string;
  canWrite: boolean;
}) {
  const { data: handoffs = [] } = useListContractHandoffsQuery(contractId);
  const [handOff, handOffState] = useHandOffContractMutation();
  const [ack, ackState] = useAckContractHandoffMutation();
  const [result, setResult] = useState<HandoffResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ackNotes, setAckNotes] = useState<Record<string, string>>({});

  async function onHandOff() {
    setError(null);
    try {
      setResult(await handOff(contractId).unwrap());
    } catch (err) {
      setResult(null);
      setError(
        errorText(err, 'Не удалось передать договор в работу. Проверьте настройку служб.'),
      );
    }
  }

  async function onAck(id: string) {
    setError(null);
    const note = ackNotes[id]?.trim();
    try {
      await ack({ id, contractId, note: note || undefined }).unwrap();
    } catch (err) {
      setError(errorText(err, 'Отметить может только участник этой службы.'));
    }
  }

  const sent = handoffs.length > 0;

  return (
    <Wrap>
      <Intro>
        Службы получат уведомление в ОСНОВЕ и письмо на почту с их обязательствами по договору —
        сроками оплаты, поставки, приёмки. Ответ на письмо придёт юристу, который ввёл договор
        в работу. Состав служб настраивает администратор в разделе «Администрация → Службы».{' '}
        <BriefLink to={`/contracts/${contractId}/brief`}>
          Открыть безопасный паспорт договора для смежных служб →
        </BriefLink>
      </Intro>

      {canWrite && (
        <Actions>
          <Button onClick={onHandOff} disabled={handOffState.isLoading}>
            {handOffState.isLoading
              ? 'Передаём…'
              : sent
                ? 'Разослать повторно'
                : 'Ввести в работу'}
          </Button>
          {result && (
            <Note $tone={result.notified > 0 ? 'ok' : 'warn'}>
              Передан службам: {result.notified > 0 ? result.services : result.services}. Уведомлены:{' '}
              {result.notified} чел.
              {result.empty_services.length > 0 &&
                ` В службах без сотрудников читать некому: ${result.empty_services.join(', ')}.`}
            </Note>
          )}
          {error && <Note $tone="err">{error}</Note>}
        </Actions>
      )}

      {sent && (
        <Table>
          <thead>
            <tr>
              <th scope="col">Служба</th>
              <th scope="col">Что передано</th>
              <th scope="col">Уведомлены</th>
              <th scope="col">Принял в работу</th>
            </tr>
          </thead>
          <tbody>
            {handoffs.map((h) => (
              <tr key={h.id}>
                <td>
                  <strong>{h.service_title}</strong>
                </td>
                <td>
                  {h.duties.length === 0 ? (
                    <Duty>Отдельных обязательств нет — уведомление о самом договоре</Duty>
                  ) : (
                    h.duties.map((d, i) => (
                      <Duty key={`${h.id}-${i}`}>
                        • {d.title}
                        {d.due_hint ? ` — ${d.due_hint}` : ''}
                        {d.clause ? ` (${d.clause})` : ''}
                      </Duty>
                    ))
                  )}
                </td>
                <td>
                  {h.notified_count > 0 ? (
                    `${h.notified_count} чел.`
                  ) : (
                    <Badge $tone="warning">нет сотрудников</Badge>
                  )}
                </td>
                <td>
                  {h.acknowledged_at ? (
                    <>
                      <Badge $tone="success">принято</Badge>
                      <Duty>
                        {h.acknowledged_by_name ?? '—'} · {when(h.acknowledged_at)}
                      </Duty>
                      {h.ack_note && <Duty>{h.ack_note}</Duty>}
                    </>
                  ) : (
                    <>
                      <Badge $tone="neutral">ожидает</Badge>
                      <AckControls>
                        <AckInput
                          type="text"
                          value={ackNotes[h.id] ?? ''}
                          onChange={(e) =>
                            setAckNotes((prev) => ({ ...prev, [h.id]: e.target.value }))
                          }
                          placeholder="Комментарий (необязательно)"
                        />
                        <Button
                          $variant="ghost"
                          $size="sm"
                          onClick={() => onAck(h.id)}
                          disabled={ackState.isLoading}
                          title="Доступно участникам этой службы"
                        >
                          Принял в работу
                        </Button>
                      </AckControls>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Wrap>
  );
}
